import React, { Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";

import SiteLayout from "./layouts/SiteLayout";
import MainLayout from "./layouts/MainLayout";

import GuestGuard from "./guards/GuestGuard";
import UserGuard from "./guards/UserGuard";

import LoadingScreen from "./components/LoadingScreen";
import { PAYMENT_SETUP_ENABLED } from "./utils/featureFlags";
import LandingPage from './views/auth/LandingPage';
import Login from "./views/auth/Login";
import Register from "./views/auth/Register";
import ForgetPass from "./views/auth/ForgetPass";
import VerifyAccountPassword from "./views/auth/ForgetPass/VerifyAccountPassword";
import LoginWithOtp from "./views/auth/Login/LoginWithOtp";

import Dashboard from "./views/user/CreatorDashboard/Dashboard";
import CreatorList from "./views/user/CreatorList/CreatorList";
import Chat from "./views/user/Chat/ChatPage";
import ActiveConversation from "./views/user/ActiveConversation/ActiveConversation";
import MySubscription from "./views/user/MySubscription/mySubscription";
import FansQuestion from "./views/user/FansQuestion/FansQuestion";
import MyEarnings from "./views/user/MyEarnings/MyEarnings";
import MyGuardrails from "./views/user/MyGuardrails/MyGuardrails";
import TrendingNow from "./views/user/TrendingNow/TrendingNow";
import PayoutHistory from "./views/user/PayoutHistory/PayoutHistory";
import PaymentSetup from "./views/user/PaymentSetup/PaymentSetup";
import VideoCall from "./views/user/VideoCall/VideoCall";
import MyAccount from "./views/user/MyProfile/MyProfile";
import Plans from "./views/user/plans/plans";
import Payment from "./views/user/payment/payment";
import PaymentSuccess from "./views/user/payment/paymentSuccess";
import PlansCancel from "./views/user/plans/cancelPlan";
import MagicLink from "./views/user/magicLink/magicLink";
import AddToHome from "./views/user/AddToHome/addToHome";
import BillingHistory from "./views/user/billingHistory/billingHistory";
import Help from "./views/user/help/help";
import TermsOfService from "./views/user/terms/TermsOfService";
import PrivacyPolicy from "./views/user/privacy/PrivacyPolicy";
import DeleteAccount from "./views/user/deleteAccount/deleteAccount";
import Settings from "./views/user/settings/settings";
import Notification from "./views/user/Notofication/notification";
import PrivacyData from "./views/user/DataProtect/privacyData";
import SubscriptionFaild from "./views/user/payment/SubscriptionFailed";
import AccountSetting from "./views/user/accountSetting/accountSettings";
import CreatorProfile from "./views/user/CreatorProfile/CreatorProfile";

import ChatLanding from "./views/user/Chat/ChatLandingPage";
import Page404 from "./views/errors/Page404";
import SSOCallback from "./views/user/ssoCallback/ssoCallBack";
import CreatorNotFound from "./views/errors/CreatorNotFound";


// ==================
// Layout Wrappers
// ==================

const GuestLayout = () => (
  <GuestGuard>
    <SiteLayout>
      <Outlet />
    </SiteLayout>
  </GuestGuard>
);

const UserLayout = () => (
  <UserGuard>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </UserGuard>
);


// ==================
// Main Routes
// ==================

export default function RenderRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>

        {/* ================= PUBLIC ROUTES ================= */}
        <Route element={<GuestLayout />}>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forget-pass" element={<ForgetPass />} />
          <Route path="/otp-verification" element={<VerifyAccountPassword />} />
          <Route path="/login-with-otp" element={<LoginWithOtp />} />
        </Route>
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route path="/:username" element={<ChatLanding />} />
        {/* ================= USER ROUTES ================= */}
        <Route element={<UserLayout />}>
          <Route path="/user" element={<Navigate to="/user/dashboard" />} />

          <Route path="/user/dashboard" element={<Dashboard />} />
          <Route path="/user/creator-list" element={<CreatorList />} />
          <Route path="/user/user-type" element={<LandingPage />} />
          <Route path="/user/chat" element={<Chat />} />
          <Route path="/user/creator-profile" element={<CreatorProfile />} />
          <Route path="/user/conversation" element={<ActiveConversation />} />
          <Route path="/user/my-subscribers" element={<MySubscription />} />
          <Route path="/user/fans-questions" element={<FansQuestion />} />
          <Route path="/user/my-earnings" element={<MyEarnings />} />
          <Route path="/user/my-guardrails" element={<MyGuardrails />} />
          <Route path="/user/trending-now" element={<TrendingNow />} />
          <Route path="/user/payout-history" element={<PayoutHistory />} />
          <Route
            path="/user/payment-setup"
            element={PAYMENT_SETUP_ENABLED ? <PaymentSetup /> : <Navigate to="/user/dashboard" replace />}
          />
          <Route path="/user/video-call" element={<VideoCall />} />
          <Route path="/user/my-profile" element={<MyAccount />} />
          <Route path="/user/subscription-plans" element={<Plans />} />
          <Route path="/user/payment" element={<Payment />} />
          <Route path="/user/success" element={<PaymentSuccess />} />
          <Route path="/user/cancel-plan" element={<PlansCancel />} />
          <Route path="/user/magic-link" element={<MagicLink />} />
          <Route path="/user/add-to-home" element={<AddToHome />} />
          <Route path="/user/billing-history" element={<BillingHistory />} />
          <Route path="/user/help" element={<Help />} />
          <Route path="/user/terms" element={<TermsOfService />} />
          <Route path="/user/privacy" element={<PrivacyPolicy />} />
          <Route path="/user/delete-account" element={<DeleteAccount />} />
          <Route path="/user/settings" element={<Settings />} />
          <Route path="/user/notification" element={<Notification />} />
          <Route path="/user/data-protect" element={<PrivacyData />} />
          <Route path="/user/failed" element={<SubscriptionFaild />} />
          <Route path="/user/manage-account" element={<AccountSetting />} />

        </Route>


        {/* ================= 404 ================= */}
        <Route path="/404" element={<Page404 />} />
        <Route path="*" element={<Navigate to="/404" />} />
        <Route path="/creator-not-found/:username" element={<CreatorNotFound />} />
      </Routes>
    </Suspense>
  );
}